import { Injectable } from '@angular/core';
import * as Intercom from '@intercom/messenger-js-sdk';
import { environment } from '../../environments/environment';
import { User, InviteStatus } from '../_models';
import {StorageService} from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class IntercomService {
  private isIntercomLoaded = false;

  constructor(protected storageService: StorageService) {}

  /**
   * Boot Intercom with user data (only call when user is authenticated)
   * @param user - Current user object
   */
  boot(user?: User): void {
    if (!environment.intercomAppId) {
      console.warn('Intercom app ID not configured');
      return;
    }

    if (!user) {
      console.warn('Cannot boot Intercom without user data');
      return;
    }

    const intercomData: any = {
      app_id: environment.intercomAppId,
      user_id: user.id ? user.id.toString() : '',
      email: user.email || '',
      name: user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : user.first_name || user.email || 'Unknown User',

      // Add custom attributes with proper data validation
      custom_attributes: {
        username: user.username || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        is_staff: user.is_staff || false,
        is_active: user.is_active || false,
        is_superuser: user.is_superuser || false,
        user_group: user.group || 'none',
        invite_status: InviteStatus[user.invite_status] || 'Unknown',
        phone: user.phone || user.test_phone || '',
        account_id: user.account && user.account.id ? user.account.id.toString() : '',
        account_name: user.account && user.account.name ? user.account.name : '',
        // Add more custom attributes as needed
      }
    };

    const intercomToken = this.storageService.get('intercomToken');
    if (intercomToken) {
      intercomData['intercom_user_jwt'] = intercomToken;
    }

    Intercom.Intercom(intercomData);
    this.isIntercomLoaded = true;
  }

  /**
   * Shutdown and clean up Intercom when user logs out
   */
  logout(): void {
    if (this.isIntercomLoaded && (window as any).Intercom) {
      (window as any).Intercom('shutdown');
      this.isIntercomLoaded = false;
    }
  }

  /**
   * Update user data in Intercom
   * @param userData - User data to update
   */
  update(userData: any): void {
    if (this.isIntercomLoaded && (window as any).Intercom) {
      (window as any).Intercom('update', userData);
    }
  }

  /**
   * Show the messenger
   */
  show(): void {
    if (this.isIntercomLoaded && (window as any).Intercom) {
      (window as any).Intercom('show');
    }
  }

  /**
   * Hide the messenger
   */
  hide(): void {
    if (this.isIntercomLoaded && (window as any).Intercom) {
      (window as any).Intercom('hide');
    }
  }

  /**
   * Show a specific article
   * @param articleId - The article ID to show
   */
  showArticle(articleId: number): void {
    if (this.isIntercomLoaded && (window as any).Intercom) {
      (window as any).Intercom('showArticle', articleId);
    }
  }

  /**
   * Show messages
   */
  showMessages(): void {
    if (this.isIntercomLoaded && (window as any).Intercom) {
      (window as any).Intercom('showMessages');
    }
  }

  /**
   * Show new message composer
   * @param message - Pre-filled message
   */
  showNewMessage(message?: string): void {
    if (this.isIntercomLoaded && (window as any).Intercom) {
      if (message) {
        (window as any).Intercom('showNewMessage', message);
      } else {
        (window as any).Intercom('showNewMessage');
      }
    }
  }

  /**
   * Track an event
   * @param eventName - Name of the event
   * @param metadata - Event metadata
   */
  trackEvent(eventName: string, metadata?: any): void {
    if (this.isIntercomLoaded && (window as any).Intercom) {
      if (metadata) {
        (window as any).Intercom('trackEvent', eventName, metadata);
      } else {
        (window as any).Intercom('trackEvent', eventName);
      }
    }
  }

  /**
   * Get the current unread conversation count
   * @param callback - Callback function to receive the count
   */
  getUnreadConversationCount(callback: (unreadCount: number) => void): void {
    if (this.isIntercomLoaded && (window as any).Intercom) {
      try {
        (window as any).Intercom('getUnreadConversationCount', callback);
      } catch (error) {
        console.warn('Could not get unread conversation count:', error);
        callback(0);
      }
    }
  }

  /**
   * Shutdown Intercom
   */
  shutdown(): void {
    if (this.isIntercomLoaded && (window as any).Intercom) {
      (window as any).Intercom('shutdown');
      this.isIntercomLoaded = false;
    }
  }

  /**
   * Check if Intercom is loaded
   */
  isLoaded(): boolean {
    return this.isIntercomLoaded;
  }

  /**
   * Start a tour
   * @param tourId - Tour ID to start
   */
  startTour(tourId: number): void {
    if (this.isIntercomLoaded && (window as any).Intercom) {
      (window as any).Intercom('startTour', tourId);
    }
  }
}
